/**
 * localStorage is the single source of truth for the session, same as the
 * wallet app — the JWT plus the few user fields the shell renders.
 */
export function saveSession({ accessToken, refreshToken, user }) {
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("userId", user?.id ?? "");
    localStorage.setItem("firstName", user?.firstName ?? "");
    localStorage.setItem("roleName", user?.roleName ?? "");
    localStorage.setItem("isSuperAdmin", String(!!user?.isSuperAdmin));
    /* Keep the whole user (incl. organization) for anything the shell needs. */
    localStorage.setItem("user", JSON.stringify(user ?? {}));
    if (user?.organization) {
        localStorage.setItem("orgName", user.organization.name ?? "");
        localStorage.setItem("orgSlug", user.organization.slug ?? "");
    }
}

export function clearSession() {
    localStorage.clear();
}

export function getUser() {
    try {
        return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
        return null;
    }
}

export const getOrgName = () => localStorage.getItem("orgName") || "";
export const getRoleName = () => localStorage.getItem("roleName") || "";

export const isSuperAdmin = () =>
    localStorage.getItem("isSuperAdmin") === "true";
export const isAdmin = () => isSuperAdmin();

/**
 * The core resources a wildcard permission does NOT cover — mirrors the
 * backend rule, so "all records" never silently means "all settings".
 */
const CORE_RESOURCES = [
    "dashboard",
    "objects",
    "pipelines",
    "workflows",
    "users",
    "roles",
    "org",
    "platform",
    "audit",
];

/**
 * Can the signed-in user perform `action` on `resource`? Same evaluation the
 * server does, used only to disable controls the API would reject anyway —
 * the server remains the authority.
 */
export function can(resource, action) {
    if (isSuperAdmin()) return true;
    const permissions = getUser()?.permissions ?? [];
    return permissions.some((p) => {
        const resourceMatches =
            p.resource === resource ||
            (p.resource === "*" && !CORE_RESOURCES.includes(resource));
        return (
            resourceMatches &&
            (p.actions?.includes("*") || p.actions?.includes(action))
        );
    });
}

/** True when there is a token and it has not expired. */
export function hasValidToken() {
    const token = localStorage.getItem("accessToken");
    if (!token) return false;
    try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.exp * 1000 > Date.now();
    } catch {
        return false;
    }
}
